import React, { useState } from 'react';
import {
  Box, TextField, IconButton, Drawer,
  Backdrop, Typography, LinearProgress
} from '@mui/material';
import Reply from '@mui/icons-material/Send';
import SelfImprovementRounded from '@mui/icons-material/SelfImprovementRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import MeditationSettings from '../../components/room/MeditationSettings';
import MeditationLimitNotice from '../../components/ads/MeditationLimitNotice';
import MessageLimitNotice from '../../components/ads/MessageLimitNotice';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const AIMessageInput = ({ onSendMessage, placeholder }) => {
  const [message, setMessage] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFreemiumDrawerOpen, setIsFreemiumDrawerOpen] = useState(false);
  const [isMessageLimitDrawerOpen, setIsMessageLimitDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const navigate = useNavigate();
  const { t } = useTranslation();

const handleSend = async () => {
  if (!message.trim()) return;

  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/check-credits`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    const data = await res.json();
    console.log('🟡 Crédit API Response:', data);

    const isSubscribed = data.subscriptionStatus === 'active';
    const hasCredits = data.monthlyMessageCredit > 0;

    if (res.ok && (isSubscribed || hasCredits)) {
      onSendMessage(message);
      setMessage('');
      if (typeof onMessageSent === 'function') {
        onMessageSent(); // ✅ Met à jour la jauge si elle est utilisée
      }
    } else {
      setIsMessageLimitDrawerOpen(true); // ❌ Bloque si non abonné + plus de crédits
    }

  } catch (err) {
    console.error('Erreur vérification crédits message :', err);
    setIsMessageLimitDrawerOpen(true);
  }
};


  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

const handleOpenSettings = async () => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/check-credits`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    const data = await res.json();

    const isSubscribed = data.subscriptionStatus === 'active';
    const hasCredits = data.monthlyMeditationCredit > 0;

    if (res.ok && (isSubscribed || hasCredits)) {
      setIsDrawerOpen(true); // ✅ Autoriser si abonné ou crédits dispo
    } else {
      setIsFreemiumDrawerOpen(true); // ❌ Sinon, drawer d’abonnement
    }
  } catch (err) {
    console.error('Erreur vérification crédits méditation :', err);
    setIsFreemiumDrawerOpen(true);
  }
};


  const handleSubmitSettings = async (settings) => {
    try {
      setIsDrawerOpen(false);
      setIsLoading(true);
      setLoadingProgress(0);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/start-meditation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(settings),
      });

      const data = await res.json();

      if (res.ok && data.sessionId) {
        const duration = Math.floor(Math.random() * 2000) + 1500;
        const steps = Math.floor(duration / 50);

        for (let i = 0; i <= steps; i++) {
          await sleep(50);
          setLoadingProgress(Math.round((i / steps) * 100));
        }

        await sleep(300);
        setIsLoading(false);
        navigate(`/solo/${data.sessionId}/live`);
      } else {
        console.error('Erreur backend', data.message);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Erreur startMeditationWithAI', err);
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ position: 'fixed', bottom: '-1px', width: '100%', backgroundColor: 'white', pb: 2, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mx: 2, backgroundColor: '#eee', borderRadius: '20px', padding: '4px', pl: '10px' }}>
          <TextField
            variant="outlined"
            placeholder={placeholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                backgroundColor: '#eee',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 12px',
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!message}
            sx={{
              marginLeft: '8px',
              color: 'black',
              backgroundColor: '#EEEEEE',
              '&:hover': { backgroundColor: '#DDDDDD' },
            }}
          >
            <Reply sx={{ fontSize: '20px' }} />
          </IconButton>
          <IconButton
            onClick={handleOpenSettings}
            sx={{
              marginLeft: '4px',
              color: 'white',
              backgroundColor: '#000',
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            <SelfImprovementRounded />
          </IconButton>
        </Box>
      </Box>

      <Drawer anchor="bottom" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
        <MeditationSettings onSubmit={handleSubmitSettings} />
      </Drawer>

      <Drawer anchor="bottom" open={isFreemiumDrawerOpen} onClose={() => setIsFreemiumDrawerOpen(false)}>
        <MeditationLimitNotice onClose={() => setIsFreemiumDrawerOpen(false)} />
      </Drawer>

      <Drawer anchor="bottom" open={isMessageLimitDrawerOpen} onClose={() => setIsMessageLimitDrawerOpen(false)}>
        <MessageLimitNotice onClose={() => setIsMessageLimitDrawerOpen(false)} />
      </Drawer>

      <Backdrop
        open={isLoading}
        sx={{
          zIndex: 1300,
          color: 'black',
          backgroundColor: 'white',
          flexDirection: 'column',
          transition: 'opacity 0.4s ease-in-out',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesomeRounded sx={{ fontSize: 24, mr: 1 }} />
          <Typography variant="h6">
            {t('Raoul prépare ta méditation')}
          </Typography>
        </Box>
        <Box sx={{ width: '80%', maxWidth: 400 }}>
          <LinearProgress
            variant="determinate"
            value={loadingProgress}
            sx={{
              height: 8,
              backgroundColor: 'secondary.main',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'black',
              },
            }}
          />
        </Box>
      </Backdrop>
    </>
  );
};

export default AIMessageInput;
